/**
 * §7.2 screen 6 — Users. Capability `user.manage`, i.e. role `admin` alone.
 *
 * The §4.4 "who may edit whom" table, as UI affordances. The server enforces
 * every row of it independently — these checks exist so an admin is never handed
 * a button that can only 403:
 *
 *  - own `role` / own `is_active`      → not offered (403 `self_role_change`)
 *  - own `/totp-reset`                 → not offered; recovery is another admin
 *                                        or the CLI break-glass path
 *  - demote/deactivate the LAST admin  → offered, but the 422 `last_admin` is
 *                                        surfaced verbatim, because "last admin"
 *                                        is server state this screen cannot
 *                                        reliably know at render time
 *  - `mahalla_scope` on an admin       → the field is hidden for role `admin`
 *  - change `email`                    → not offered at all; it is the login
 *                                        identity and appears in audit_log
 *  - DELETE a user                     → does not exist; retirement is is_active
 *
 * The `otpauth://` URI is rendered as a QR **in the browser** and never leaves
 * it: the API returns it exactly once and does not store it recoverably.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { ADMIN_ROLES, type AdminRole } from '@navoiy/domain';
import { ApiError, api, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDateTime } from '../lib/format.js';
import { useMahallas } from '../lib/useMahallas.js';
import { QrCode } from '../components/QrCode.jsx';
import { PasswordDialog } from '../components/PasswordDialog.jsx';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorBanner,
  Field,
  Modal,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  TextInput,
  Tr,
  WarningBanner,
} from '../ui/index.jsx';

type AdminUser = components['schemas']['AdminUserAdmin'];

export function UsersScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { me, expire } = useSession();
  const { mahallas } = useMahallas(locale);

  const [rows, setRows] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [passwordFor, setPasswordFor] = useState<AdminUser | null>(null);
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof ApiError && caught.status === 401) return expire();
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    },
    [expire, t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = unwrap(await api().GET('/admin/users'));
      setRows(data.items);
      setError(null);
    } catch (caught) {
      handle(caught);
    } finally {
      setLoading(false);
    }
  }, [handle]);

  useEffect(() => {
    void load();
  }, [load]);

  async function resetTotp(user: AdminUser): Promise<void> {
    if (!window.confirm(t('admin.users.resetTotpConfirm'))) return;
    try {
      const data = unwrap(
        await api().POST('/admin/users/{user_id}/totp-reset', {
          params: { path: { user_id: user.id } },
        }),
      );
      setProvisioningUri(data.totp_provisioning_uri);
      await load();
    } catch (caught) {
      handle(caught);
    }
  }

  async function unlock(user: AdminUser): Promise<void> {
    try {
      unwrap(
        await api().PATCH('/admin/users/{user_id}', {
          params: { path: { user_id: user.id } },
          body: { unlock: true },
        }),
      );
      await load();
    } catch (caught) {
      handle(caught);
    }
  }

  return (
    <Card
      title={t('admin.users.title')}
      actions={<Button onClick={() => setCreating(true)}>{t('admin.users.addUser')}</Button>}
    >
      <ErrorBanner message={error} />
      {loading ? <Spinner label={t('admin.common.loading')} /> : null}
      {!loading && rows.length === 0 ? <EmptyState message={t('admin.users.empty')} /> : null}

      {rows.length > 0 ? (
        <Table
          head={
            <tr>
              <Th>{t('admin.users.email')}</Th>
              <Th>{t('admin.users.fullName')}</Th>
              <Th>{t('admin.users.role')}</Th>
              <Th>{t('admin.users.mahallaScope')}</Th>
              <Th>{t('admin.users.lastLogin')}</Th>
              <Th>{t('admin.users.isActive')}</Th>
              <Th>{t('admin.common.actions')}</Th>
            </tr>
          }
        >
          {rows.map((user) => {
            const isSelf = user.id === me?.id;
            return (
              <Tr key={user.id}>
                <Td className="font-semibold">{user.email}</Td>
                <Td>{user.full_name}</Td>
                <Td>{t(`admin.role.${user.role}`)}</Td>
                <Td>
                  {user.mahalla_scope?.length
                    ? `${user.mahalla_scope.length}`
                    : t('admin.users.allMahallas')}
                </Td>
                <Td className="whitespace-nowrap">
                  {user.last_login_at ? formatDateTime(user.last_login_at) : t('admin.common.never')}
                </Td>
                <Td>
                  <span className="flex flex-col gap-1">
                    <Badge tone={user.is_active ? 'success' : 'neutral'}>
                      {user.is_active ? t('admin.common.yes') : t('admin.common.no')}
                    </Badge>
                    {!user.totp_confirmed_at ? (
                      <Badge tone="gold">{t('admin.users.totpPending')}</Badge>
                    ) : null}
                    {user.locked_until ? (
                      <Badge tone="danger">
                        {t('admin.users.locked', { until: formatDateTime(user.locked_until) })}
                      </Badge>
                    ) : null}
                  </span>
                </Td>
                <Td>
                  <span className="flex flex-wrap gap-2">
                    <Button tone="ghost" onClick={() => setEditing(user)}>
                      {t('admin.common.edit')}
                    </Button>
                    <Button tone="ghost" onClick={() => setPasswordFor(user)}>
                      {isSelf ? t('admin.users.changePassword') : t('admin.users.resetPassword')}
                    </Button>
                    {/* §4.4 — never on oneself: a second factor that resets
                        itself from an authenticated session is not a factor. */}
                    {!isSelf ? (
                      <Button tone="ghost" onClick={() => void resetTotp(user)}>
                        {t('admin.users.resetTotp')}
                      </Button>
                    ) : null}
                    {user.locked_until ? (
                      <Button tone="ghost" onClick={() => void unlock(user)}>
                        {t('admin.users.unlock')}
                      </Button>
                    ) : null}
                  </span>
                </Td>
              </Tr>
            );
          })}
        </Table>
      ) : null}

      {creating ? (
        <CreateUserDialog
          mahallaOptions={mahallas.map((m) => ({ id: m.id, name: m.name }))}
          onClose={() => setCreating(false)}
          onCreated={(uri) => {
            setCreating(false);
            setProvisioningUri(uri);
            void load();
          }}
          onError={handle}
        />
      ) : null}

      {editing ? (
        <EditUserDialog
          user={editing}
          isSelf={editing.id === me?.id}
          mahallaOptions={mahallas.map((m) => ({ id: m.id, name: m.name }))}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
          onError={handle}
        />
      ) : null}

      {passwordFor ? (
        <PasswordDialog
          userId={passwordFor.id}
          self={passwordFor.id === me?.id}
          onClose={() => setPasswordFor(null)}
        />
      ) : null}

      {provisioningUri ? (
        <Modal
          title={t('admin.users.qrTitle')}
          onClose={() => setProvisioningUri(null)}
          footer={<Button onClick={() => setProvisioningUri(null)}>{t('admin.common.close')}</Button>}
        >
          <WarningBanner>{t('admin.users.qrOnce')}</WarningBanner>
          <div className="flex justify-center">
            <QrCode value={provisioningUri} />
          </div>
          <code className="block break-all rounded-chip bg-surface-100 p-3 text-xs text-ink-700">
            {provisioningUri}
          </code>
        </Modal>
      ) : null}
    </Card>
  );
}

function MahallaScopePicker({
  role,
  scope,
  options,
  onChange,
}: {
  role: AdminRole;
  scope: string[];
  options: { id: string; name: string }[];
  onChange: (next: string[]) => void;
}): React.JSX.Element | null {
  const { t } = useI18n();
  // §4.4 — mahalla_scope is meaningless for an admin and is rejected with 422
  // `not_allowed_for_role`, so the field is not offered at all for that role.
  if (role === 'admin') return null;

  return (
    <Field label={t('admin.users.mahallaScope')} hint={t('admin.users.allMahallas')}>
      <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-chip border-1 border-line-300 p-3">
        {options.map((option) => (
          <Checkbox
            key={option.id}
            label={option.name}
            checked={scope.includes(option.id)}
            onChange={(event) =>
              onChange(
                event.target.checked
                  ? [...scope, option.id]
                  : scope.filter((id) => id !== option.id),
              )
            }
          />
        ))}
      </div>
    </Field>
  );
}

function CreateUserDialog({
  mahallaOptions,
  onClose,
  onCreated,
  onError,
}: {
  mahallaOptions: { id: string; name: string }[];
  onClose: () => void;
  onCreated: (provisioningUri: string) => void;
  onError: (error: unknown) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<AdminRole>('viewer');
  const [password, setPassword] = useState('');
  const [scope, setScope] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      const data = unwrap(
        await api().POST('/admin/users', {
          body: {
            email,
            full_name: fullName,
            role,
            password,
            ...(role !== 'admin' && scope.length > 0 ? { mahalla_scope: scope } : {}),
          },
        }),
      );
      onCreated(data.totp_provisioning_uri);
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('admin.users.addUser')}
      onClose={onClose}
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void save()}>
            {t('admin.common.create')}
          </Button>
        </>
      }
    >
      <Field label={t('admin.users.email')} hint={t('admin.users.emailImmutable')}>
        <TextInput
          type="email"
          required
          maxLength={254}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field label={t('admin.users.fullName')}>
        <TextInput
          required
          maxLength={200}
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
      </Field>
      <Field label={t('admin.users.role')}>
        <Select value={role} onChange={(event) => setRole(event.target.value as AdminRole)}>
          {ADMIN_ROLES.map((value) => (
            <option key={value} value={value}>
              {t(`admin.role.${value}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('admin.users.password')} hint={t('admin.users.passwordHint')}>
        <TextInput
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          maxLength={256}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      <MahallaScopePicker role={role} scope={scope} options={mahallaOptions} onChange={setScope} />
    </Modal>
  );
}

function EditUserDialog({
  user,
  isSelf,
  mahallaOptions,
  onClose,
  onSaved,
  onError,
}: {
  user: AdminUser;
  isSelf: boolean;
  mahallaOptions: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
  onError: (error: unknown) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [fullName, setFullName] = useState(user.full_name);
  const [role, setRole] = useState<AdminRole>(user.role);
  const [isActive, setIsActive] = useState(user.is_active);
  const [scope, setScope] = useState<string[]>(user.mahalla_scope ?? []);
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    if (!isActive && user.is_active && !window.confirm(t('admin.users.deactivateConfirm'))) return;
    setBusy(true);
    try {
      unwrap(
        await api().PATCH('/admin/users/{user_id}', {
          params: { path: { user_id: user.id } },
          body: {
            full_name: fullName,
            // §4.4 — an admin may not change their OWN role or is_active.
            ...(isSelf ? {} : { role, is_active: isActive }),
            ...(role !== 'admin' && !isSelf ? { mahalla_scope: scope } : {}),
          },
        }),
      );
      onSaved();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('admin.users.editUser')}
      onClose={onClose}
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void save()}>
            {t('admin.common.save')}
          </Button>
        </>
      }
    >
      {isSelf ? <WarningBanner>{t('admin.users.selfNoRoleChange')}</WarningBanner> : null}

      <Field label={t('admin.users.email')} hint={t('admin.users.emailImmutable')}>
        <TextInput value={user.email} disabled />
      </Field>

      <Field label={t('admin.users.fullName')}>
        <TextInput
          required
          maxLength={200}
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
      </Field>

      <Field label={t('admin.users.role')}>
        <Select
          value={role}
          disabled={isSelf}
          onChange={(event) => setRole(event.target.value as AdminRole)}
        >
          {ADMIN_ROLES.map((value) => (
            <option key={value} value={value}>
              {t(`admin.role.${value}`)}
            </option>
          ))}
        </Select>
      </Field>

      {!isSelf ? (
        <>
          <MahallaScopePicker
            role={role}
            scope={scope}
            options={mahallaOptions}
            onChange={setScope}
          />
          <Checkbox
            label={t('admin.users.isActive')}
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
        </>
      ) : (
        <p className="text-xs text-ink-500">{t('admin.users.selfNoTotpReset')}</p>
      )}
    </Modal>
  );
}
